import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChatCircleDots, ClockCounterClockwise, Folders, Bell, Gear, SignOut, Flask, ChartLineUp } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import GkLogo from "@/components/GkLogo";
import PoweredBy from "@/components/PoweredBy";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
    isActive ? "bg-gk-red text-white" : "text-ink-soft hover:text-gk-charcoal hover:bg-surface"
  }`;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { preview_mode } = useConfig();
  const nav = useNavigate();
  const isManager = user?.role === "manager";

  const doLogout = () => { logout(); nav("/login"); };

  return (
    <div className={`min-h-screen bg-canvas text-ink flex ${preview_mode ? "pt-7" : ""}`}>
      {preview_mode && (
        <div
          data-testid="preview-banner"
          className="fixed top-0 left-0 right-0 z-50 bg-gk-red text-white text-[12px] font-medium py-1.5 px-4 flex items-center justify-center gap-2 shadow-sm"
        >
          <Flask size={13} weight="fill" />
          <span>Preview Mode — Microsoft/Outlook SSO not required · Google Drive not connected · Notifications are mocked (no real emails sent)</span>
        </div>
      )}
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex md:flex-col w-[280px] border-r border-line bg-surface/50 h-screen sticky top-0">
        <div className="px-4 py-3 border-b border-line">
          <GkLogo variant="horizontal" className="w-full h-auto max-h-24 object-contain" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1 text-center">Ask Tracey · Internal Assistant</div>
        </div>
        <nav className="p-3 flex-1 space-y-1" data-testid="sidebar-nav">
          <NavLink to="/" end className={linkCls} data-testid="nav-chat">
            <ChatCircleDots size={18} weight="duotone" /> New chat
          </NavLink>
          <NavLink to="/history" className={linkCls} data-testid="nav-history">
            <ClockCounterClockwise size={18} weight="duotone" /> History
          </NavLink>
          {isManager && (
            <>
              <div className="px-3 pt-5 pb-1 text-[11px] uppercase tracking-widest text-ink-soft">Manager</div>
              <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
                <Folders size={18} weight="duotone" /> Knowledge base
              </NavLink>
              <NavLink to="/notifications" className={linkCls} data-testid="nav-notifications">
                <Bell size={18} weight="duotone" /> Alerts
              </NavLink>
              <NavLink to="/team-usage" className={linkCls} data-testid="nav-team-usage">
                <ChartLineUp size={18} weight="duotone" /> Team Usage
              </NavLink>
              <NavLink to="/settings" className={linkCls} data-testid="nav-settings">
                <Gear size={18} weight="duotone" /> Settings
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-line">
          <div className="px-3 py-2 text-sm">
            <div className="font-medium truncate text-gk-charcoal">{user?.name}</div>
            <div className="text-xs text-ink-soft truncate">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gk-red/10 text-gk-red">
              {user?.role}
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-ink-soft hover:text-gk-red hover:bg-surface">
            <SignOut size={16} weight="duotone" /> Sign out
          </button>
          <div className="mt-3 pt-3 border-t border-line/70">
            <PoweredBy variant="sidebar" />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col pb-16 md:pb-0">
        {children}
      </main>

      {/* Bottom nav - mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass-header px-2 py-2 flex justify-around" data-testid="bottom-nav">
        <MobileNavItem to="/" icon={<ChatCircleDots size={22} weight="duotone" />} label="Chat" testid="mnav-chat" />
        <MobileNavItem to="/history" icon={<ClockCounterClockwise size={22} weight="duotone" />} label="History" testid="mnav-history" />
        {isManager && <MobileNavItem to="/admin" icon={<Folders size={22} weight="duotone" />} label="Docs" testid="mnav-admin" />}
        {isManager && <MobileNavItem to="/notifications" icon={<Bell size={22} weight="duotone" />} label="Alerts" testid="mnav-notif" />}
        {isManager && <MobileNavItem to="/team-usage" icon={<ChartLineUp size={22} weight="duotone" />} label="Usage" testid="mnav-team-usage" />}
        {isManager && <MobileNavItem to="/settings" icon={<Gear size={22} weight="duotone" />} label="Settings" testid="mnav-settings" />}
      </nav>
    </div>
  );
}

function MobileNavItem({ to, icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testid}
      className={({ isActive }) =>
        `flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-[11px] ${
          isActive ? "text-gk-red" : "text-ink-soft"
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
